// src/pages/About/OurService.jsx
import React, { useState } from 'react';
import Header from '../../components/common/Header/Header';
import Footer from '../../components/common/Footer/Footer';
import Layout from '../../components/common/Layout/Layout';
import { ChevronDown } from 'lucide-react';
import QuerySection from '../../components/common/QuerySection';

const OurApproach = () => {
    const [activeDropdown, setActiveDropdown] = useState(null);

    const faqItems = [
        { id: 1, question: "What are the tax benefits of mutual fund investments?" },
        { id: 2, question: "How does systematic investment planning (SIP) work?" },
        { id: 3, question: "What risks are covered under portfolio insurance?" },
        { id: 4, question: "What are the benefits of long-term investment planning?" }
    ];

    const toggleDropdown = (id) => {
        setActiveDropdown(activeDropdown === id ? null : id);
    };

    return (
        <>
            <style>{`
                * {
                    box-sizing: border-box;
                    margin: 0;
                    padding: 0;
                }
                
                /* Performance optimizations */
                .approach-card, .hero-container, .treasure-container {
                    will-change: transform;
                    transform: translateZ(0);
                }
                
                /* Smooth transitions */
                .approach-card {
                    transition: transform 0.2s ease;
                }

                .approach-card:hover {
                    transform: translateY(-3px);
                }
                
                /* Mobile-first responsive design */
                .hero-container {
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    text-align: center;
                    gap: 2rem;
                    padding: 1rem;
                }
                
                .approach-grid {
                    display: grid;
                    grid-template-columns: 1fr;
                    gap: 1.5rem;
                }
                
                /* Tablet styles */
                @media (min-width: 768px) {
                    .hero-container {
                        padding: 2rem;
                        gap: 3rem;
                    }
                    
                    .approach-grid {
                        grid-template-columns: repeat(2, 1fr);
                        gap: 2rem;
                    }
                }
                
                /* Desktop styles */
                @media (min-width: 1024px) {
                    .hero-container {
                        flex-direction: row;
                        text-align: left;
                        gap: 4rem;
                        min-height: 400px;
                    }
                    
                    .approach-grid {
                        grid-template-columns: repeat(3, 1fr);
                    }
                }
                
                /* Typography scaling */
                .hero-title {
                    font-size: clamp(2rem, 5vw, 4rem);
                    line-height: 1.2;
                }
                
                .section-title {
                    font-size: clamp(1.5rem, 4vw, 2.5rem);
                }
                
                .card-title {
                    font-size: clamp(1.1rem, 3vw, 1.5rem);
                }
                
                /* Image optimization */
                img {
                    max-width: 100%;
                    height: auto;
                }
                
                /* Safe area for iOS devices */
                @supports (padding: max(0px)) {
                    .hero-container, .treasure-container, .approach-container {
                        padding-left: max(1rem, env(safe-area-inset-left));
                        padding-right: max(1rem, env(safe-area-inset-right));
                    }
                }
            `}</style>

            <div style={{ minHeight: '100vh', backgroundColor: '#ffffff' }}>
                {/* Header */}
                <Header />

                {/* Hero Section */}
                <section style={{
                    backgroundImage: 'url(https://images.unsplash.com/photo-1554224155-6726b3ff858f?ixlib=rb-4.0.3&auto=format&fit=crop&w=1920&q=80)',
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                    padding: 'clamp(3rem, 8vw, 6rem) 0',
                    position: 'relative',
                    minHeight: 'clamp(400px, 60vh, 500px)',
                    border: '2px solid rgba(255, 255, 255, 0.1)'
                }}>
                    {/* Glass overlay with gradient - no blur */}
                    <div style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        background: 'linear-gradient(135deg, rgba(0, 0, 0, 0.4) 0%, rgba(59, 130, 246, 0.3) 50%, rgba(0, 0, 0, 0.5) 100%)',
                        border: '1px solid rgba(255, 255, 255, 0.2)'
                    }} />
                    
                    <div style={{
                        maxWidth: '1200px',
                        margin: '0 auto',
                        position: 'relative',
                        zIndex: 1
                    }} className="hero-container">
                        <div style={{ 
                            textAlign: 'center',
                            maxWidth: '800px',
                            margin: '0 auto'
                        }}>
                            <h1 style={{
                                fontWeight: '700',
                                color: '#ffffff',
                                margin: '0 0 1rem 0',
                                textShadow: '0 2px 4px rgba(0, 0, 0, 0.3)'
                            }} className="hero-title">
                                Our Investment Approach
                            </h1>
                            <p style={{
                                fontSize: 'clamp(1rem, 2.5vw, 1.3rem)',
                                color: '#f3f4f6',
                                margin: '0',
                                lineHeight: '1.6',
                                maxWidth: '600px',
                                marginLeft: 'auto',
                                marginRight: 'auto',
                                textShadow: '0 1px 2px rgba(0, 0, 0, 0.3)'
                            }}>
                                Creating value through strategic investment solutions designed for long-term financial growth*
                            </p>
                        </div>
                    </div>
                </section>

                {/* Investment Approach Section */}
                <section style={{
                    backgroundColor: '#f8fafc',
                    padding: 'clamp(3rem, 8vw, 6rem) 0'
                }} className="approach-section">
                    <div style={{
                        maxWidth: '1200px',
                        margin: '0 auto',
                        padding: '0 1rem'
                    }} className="approach-container">
                        <div style={{
                            textAlign: 'center',
                            marginBottom: 'clamp(2rem, 6vw, 4rem)'
                        }}>
                            <h2 style={{
                                fontWeight: '700',
                                color: '#1a1a1a',
                                marginBottom: '1rem'
                            }} className="section-title">
                                Our Investment Philosophy
                            </h2>
                            <p style={{
                                fontSize: 'clamp(1rem, 2vw, 1.1rem)',
                                color: '#6b7280',
                                maxWidth: '600px',
                                margin: '0 auto'
                            }}>
                                We follow a comprehensive approach to investment planning, focusing on your unique financial objectives*
                            </p>
                        </div>

                        <div className="approach-grid">
                            {/* Portfolio Size Analysis */}
                            <div style={{
                                backgroundImage: 'url(https://images.unsplash.com/photo-1590283603385-17ffb3a7f29f?ixlib=rb-4.0.3&auto=format&fit=crop&w=500&q=80)',
                                backgroundSize: 'cover',
                                backgroundPosition: 'center',
                                borderRadius: '12px',
                                padding: '2rem',
                                position: 'relative',
                                overflow: 'hidden',
                                minHeight: '250px',
                                border: '2px solid rgba(255, 255, 255, 0.2)'
                            }} className="approach-card">
                                <div style={{
                                    position: 'absolute',
                                    top: 0,
                                    left: 0,
                                    right: 0,
                                    bottom: 0,
                                    background: 'linear-gradient(135deg, rgba(0, 0, 0, 0.4) 0%, rgba(0, 0, 0, 0.6) 100%)'
                                }} />
                                <div style={{ position: 'relative', zIndex: 1 }}>
                                    <h3 style={{
                                        fontWeight: '700',
                                        color: '#ffffff',
                                        marginBottom: '1rem',
                                        textShadow: '0 2px 4px rgba(0, 0, 0, 0.3)'
                                    }} className="card-title">
                                        Portfolio Size Analysis
                                    </h3>
                                    <p style={{
                                        color: '#f3f4f6',
                                        lineHeight: '1.6',
                                        fontSize: 'clamp(0.9rem, 2vw, 1rem)',
                                        textShadow: '0 1px 2px rgba(0, 0, 0, 0.3)'
                                    }}>
                                        We evaluate your investment capacity to design appropriate asset allocation strategies. Past performance may not be indicative of future results.*
                                    </p>
                                </div>
                            </div>

                            {/* Market Momentum */}
                            <div style={{
                                backgroundImage: 'url(https://images.unsplash.com/photo-1611974789855-9c2a0a7236a3?ixlib=rb-4.0.3&auto=format&fit=crop&w=500&q=80)',
                                backgroundSize: 'cover',
                                backgroundPosition: 'center',
                                borderRadius: '12px',
                                padding: '2rem',
                                position: 'relative',
                                overflow: 'hidden',
                                minHeight: '250px',
                                border: '2px solid rgba(255, 255, 255, 0.2)'
                            }} className="approach-card">
                                <div style={{
                                    position: 'absolute',
                                    top: 0,
                                    left: 0,
                                    right: 0,
                                    bottom: 0,
                                    background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.3) 0%, rgba(0, 0, 0, 0.5) 100%)'
                                }} />
                                <div style={{ position: 'relative', zIndex: 1 }}>
                                    <h3 style={{
                                        fontWeight: '700',
                                        color: '#ffffff',
                                        marginBottom: '1rem',
                                        textShadow: '0 2px 4px rgba(0, 0, 0, 0.3)'
                                    }} className="card-title">
                                        Market Analysis
                                    </h3>
                                    <p style={{
                                        color: '#f3f4f6',
                                        lineHeight: '1.6',
                                        fontSize: 'clamp(0.9rem, 2vw, 1rem)',
                                        textShadow: '0 1px 2px rgba(0, 0, 0, 0.3)'
                                    }}>
                                        We monitor market trends to identify investment opportunities. Market conditions are subject to volatility and risk.*
                                    </p>
                                </div>
                            </div>

                            {/* Valuation Assessment */}
                            <div style={{
                                backgroundImage: 'url(https://images.unsplash.com/photo-1460925895917-afdab827c52f?ixlib=rb-4.0.3&auto=format&fit=crop&w=500&q=80)',
                                backgroundSize: 'cover',
                                backgroundPosition: 'center',
                                borderRadius: '12px',
                                padding: '2rem',
                                position: 'relative',
                                overflow: 'hidden',
                                minHeight: '250px',
                                border: '2px solid rgba(255, 255, 255, 0.2)'
                            }} className="approach-card">
                                <div style={{
                                    position: 'absolute',
                                    top: 0,
                                    left: 0,
                                    right: 0,
                                    bottom: 0,
                                    background: 'linear-gradient(135deg, rgba(245, 158, 11, 0.2) 0%, rgba(0, 0, 0, 0.6) 100%)'
                                }} />
                                <div style={{ position: 'relative', zIndex: 1 }}>
                                    <h3 style={{
                                        fontWeight: '700',
                                        color: '#ffffff',
                                        marginBottom: '1rem',
                                        textShadow: '0 2px 4px rgba(0, 0, 0, 0.3)'
                                    }} className="card-title">
                                        Asset Research
                                    </h3>
                                    <p style={{
                                        color: '#f3f4f6',
                                        lineHeight: '1.6',
                                        fontSize: 'clamp(0.9rem, 2vw, 1rem)',
                                        textShadow: '0 1px 2px rgba(0, 0, 0, 0.3)'
                                    }}>
                                        We perform analysis of investment opportunities based on available data. All investments carry inherent risks.*
                                    </p>
                                </div>
                            </div>

                            {/* Investment Stage Planning */}
                            <div style={{
                                backgroundImage: 'url(https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?ixlib=rb-4.0.3&auto=format&fit=crop&w=500&q=80)',
                                backgroundSize: 'cover',
                                backgroundPosition: 'center',
                                borderRadius: '12px',
                                padding: '2rem',
                                position: 'relative',
                                overflow: 'hidden',
                                minHeight: '250px',
                                border: '2px solid rgba(255, 255, 255, 0.2)'
                            }} className="approach-card">
                                <div style={{
                                    position: 'absolute',
                                    top: 0,
                                    left: 0,
                                    right: 0,
                                    bottom: 0,
                                    background: 'linear-gradient(135deg, rgba(239, 68, 68, 0.2) 0%, rgba(0, 0, 0, 0.5) 100%)'
                                }} />
                                <div style={{ position: 'relative', zIndex: 1 }}>
                                    <h3 style={{
                                        fontWeight: '700',
                                        color: '#ffffff',
                                        marginBottom: '1rem',
                                        textShadow: '0 2px 4px rgba(0, 0, 0, 0.3)'
                                    }} className="card-title">
                                        Personalized Planning
                                    </h3>
                                    <p style={{
                                        color: '#f3f4f6',
                                        lineHeight: '1.6',
                                        fontSize: 'clamp(0.9rem, 2vw, 1rem)',
                                        textShadow: '0 1px 2px rgba(0, 0, 0, 0.3)'
                                    }}>
                                        We customize investment strategies based on your risk profile and financial objectives. Please consider your risk tolerance.*
                                    </p>
                                </div>
                            </div>

                            {/* Consistent Returns Focus */}
                            <div style={{
                                backgroundImage: 'url(https://images.unsplash.com/photo-1551288049-bebda4e38f71?ixlib=rb-4.0.3&auto=format&fit=crop&w=500&q=80)',
                                backgroundSize: 'cover',
                                backgroundPosition: 'center',
                                borderRadius: '12px',
                                padding: '2rem',
                                position: 'relative',
                                overflow: 'hidden',
                                minHeight: '250px',
                                border: '2px solid rgba(255, 255, 255, 0.2)'
                            }} className="approach-card">
                                <div style={{
                                    position: 'absolute',
                                    top: 0,
                                    left: 0,
                                    right: 0,
                                    bottom: 0,
                                    background: 'linear-gradient(135deg, rgba(147, 51, 234, 0.2) 0%, rgba(0, 0, 0, 0.6) 100%)'
                                }} />
                                <div style={{ position: 'relative', zIndex: 1 }}>
                                    <h3 style={{
                                        fontWeight: '700',
                                        color: '#ffffff',
                                        marginBottom: '1rem',
                                        textShadow: '0 2px 4px rgba(0, 0, 0, 0.3)'
                                    }} className="card-title">
                                    Long-term Strategy
                                    </h3>
                                    <p style={{
                                        color: '#f3f4f6',
                                        lineHeight: '1.6',
                                        fontSize: 'clamp(0.9rem, 2vw, 1rem)',
                                        textShadow: '0 1px 2px rgba(0, 0, 0, 0.3)'
                                    }}>
                                        We focus on disciplined investing over time horizons. Returns are not guaranteed and investments are subject to market risks.*
                                    </p>
                                </div>
                            </div>

                            {/* Risk Management */}
                            <div style={{
                                backgroundImage: 'url(https://images.unsplash.com/photo-1620714223084-8fcacc6dfd8d?ixlib=rb-4.0.3&auto=format&fit=crop&w=500&q=80)',
                                backgroundSize: 'cover',
                                backgroundPosition: 'center',
                                borderRadius: '12px',
                                padding: '2rem',
                                position: 'relative',
                                overflow: 'hidden',
                                minHeight: '250px',
                                border: '2px solid rgba(255, 255, 255, 0.2)'
                            }} className="approach-card">
                                <div style={{
                                    position: 'absolute',
                                    top: 0,
                                    left: 0,
                                    right: 0,
                                    bottom: 0,
                                    background: 'linear-gradient(135deg, rgba(6, 182, 212, 0.2) 0%, rgba(0, 0, 0, 0.5) 100%)'
                                }} />
                                <div style={{ position: 'relative', zIndex: 1 }}>
                                    <h3 style={{
                                        fontWeight: '700',
                                        color: '#ffffff',
                                        marginBottom: '1rem',
                                        textShadow: '0 2px 4px rgba(0, 0, 0, 0.3)'
                                    }} className="card-title">
                                        Risk Assessment
                                    </h3>
                                    <p style={{
                                        color: '#f3f4f6',
                                        lineHeight: '1.6',
                                        fontSize: 'clamp(0.9rem, 2vw, 1rem)',
                                        textShadow: '0 1px 2px rgba(0, 0, 0, 0.3)'
                                    }}>
                                        We implement risk management strategies as part of portfolio management. All mutual fund investments are subject to market risks.*
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>

                {/* Wealth Management Section */}
                <section style={{
                    backgroundColor: '#ffffff',
                    padding: 'clamp(3rem, 8vw, 6rem) 0'
                }} className="treasure-section">
                    <div style={{
                        maxWidth: '1200px',
                        margin: '0 auto',
                        padding: '0 1rem'
                    }} className="treasure-container">
                        <div style={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'flex-start',
                            gap: 'clamp(2rem, 4vw, 4rem)',
                            marginBottom: 'clamp(2rem, 4vw, 4rem)'
                        }}>
                            <div style={{ width: '100%', maxWidth: '400px' }}>
                                <h2 style={{
                                    fontWeight: '700',
                                    color: '#1a1a1a',
                                    margin: '0',
                                    lineHeight: '1.2'
                                }} className="section-title">
                                    Investment Planning Services
                                </h2>
                            </div>
                            <div style={{ flex: 1 }}>
                                <p style={{
                                    fontSize: 'clamp(1rem, 2vw, 1.1rem)',
                                    color: '#4b5563',
                                    lineHeight: '1.6',
                                    marginBottom: '1.5rem'
                                }}>
                                    At WinNest, we provide mutual fund investment services through research and analysis. Our solutions are designed using available market data and personalized guidance to help you work towards your financial goals.*
                                </p>
                                <p style={{
                                    fontSize: 'clamp(1rem, 2vw, 1.2rem)',
                                    fontWeight: '600',
                                    color: '#1a1a1a'
                                }}>
                                    Building disciplined investment habits for the future.*
                                </p>
                            </div>
                        </div>
                        <div>
                            <img 
                                src="https://images.unsplash.com/photo-1460925895917-afdab827c52f?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80" 
                                alt="Investment planning and analysis" 
                                style={{
                                    width: '100%',
                                    height: 'clamp(250px, 40vw, 400px)',
                                    objectFit: 'cover',
                                    borderRadius: '12px',
                                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)'
                                }}
                                loading="lazy"
                            />
                        </div>
                    </div>
                </section>

                {/* Portfolio Management Section */}
                <section style={{
                    backgroundColor: '#ffffff',
                    padding: 'clamp(3rem, 8vw, 6rem) 0'
                }}>
                    <div style={{
                        maxWidth: '1200px',
                        margin: '0 auto',
                        padding: '0 1rem'
                    }}>
                        <div style={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 'clamp(2rem, 4vw, 4rem)'
                        }}>
                            <div style={{ width: '100%' }}>
                                <img 
                                    src="https://images.unsplash.com/photo-1551288049-bebda4e38f71?ixlib=rb-4.0.3&auto=format&fit=crop&w=600&q=80" 
                                    alt="Portfolio analysis and management" 
                                    style={{
                                        width: '100%',
                                        height: 'clamp(200px, 35vw, 350px)',
                                        objectFit: 'cover',
                                        borderRadius: '12px',
                                        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)'
                                    }}
                                    loading="lazy"
                                />
                            </div>
                            <div style={{ width: '100%' }}>
                                <h2 style={{
                                    fontWeight: '700',
                                    color: '#1a1a1a',
                                    marginBottom: '2rem',
                                    lineHeight: '1.2'
                                }} className="section-title">
                                    Portfolio Management Process*
                                </h2>
                                
                                <div style={{ marginBottom: '2rem' }}>
                                    <h4 style={{
                                        fontSize: 'clamp(1rem, 2vw, 1.1rem)',
                                        fontWeight: '700',
                                        color: '#1a1a1a',
                                        marginBottom: '0.5rem'
                                    }}>
                                        Review Current Holdings:
                                    </h4>
                                    <p style={{
                                        fontSize: 'clamp(0.9rem, 2vw, 1rem)',
                                        color: '#6b7280',
                                        lineHeight: '1.6'
                                    }}>
                                        We analyze your existing investment mix based on available data and compare it against target allocation parameters.*
                                    </p>
                                </div>

                                <div style={{ marginBottom: '2rem' }}>
                                    <h4 style={{
                                        fontSize: 'clamp(1rem, 2vw, 1.1rem)',
                                        fontWeight: '700',
                                        color: '#1a1a1a',
                                        marginBottom: '0.5rem'
                                    }}>
                                        Assess Allocation:
                                    </h4>
                                    <p style={{
                                        fontSize: 'clamp(0.9rem, 2vw, 1rem)',
                                        color: '#6b7280',
                                        lineHeight: '1.6'
                                    }}>
                                        We determine which asset classes may need adjustment based on market movements and portfolio objectives.*
                                    </p>
                                </div>

                                <div style={{ marginBottom: '2rem' }}>
                                    <h4 style={{
                                        fontSize: 'clamp(1rem, 2vw, 1.1rem)',
                                        fontWeight: '700',
                                        color: '#1a1a1a',
                                        marginBottom: '0.5rem'
                                    }}>
                                        Strategic Implementation:
                                    </h4>
                                    <p style={{
                                        fontSize: 'clamp(0.9rem, 2vw, 1rem)',
                                        color: '#6b7280',
                                        lineHeight: '1.6'
                                    }}>
                                        We suggest adjustments to help align your portfolio with investment objectives, subject to your approval.*
                                    </p>
                                </div>

                                <div>
                                    <h4 style={{
                                        fontSize: 'clamp(1rem, 2vw, 1.1rem)',
                                        fontWeight: '700',
                                        color: '#1a1a1a',
                                        marginBottom: '0.5rem'
                                    }}>
                                        Monitor and Report:
                                    </h4>
                                    <p style={{
                                        fontSize: 'clamp(0.9rem, 2vw, 1rem)',
                                        color: '#6b7280',
                                        lineHeight: '1.6'
                                    }}>
                                        We provide reports on portfolio activities and continue monitoring for potential future adjustments.*
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>

                {/* AMFI Disclaimer */}
                <section style={{
                    backgroundColor: '#f8fafc',
                    padding: '2rem 0',
                    borderTop: '1px solid #e5e7eb'
                }}>
                    <div style={{
                        maxWidth: '1200px',
                        margin: '0 auto',
                        padding: '0 1rem',
                        textAlign: 'center'
                    }}>
                        <p style={{
                            fontSize: '0.875rem',
                            color: '#6b7280',
                            lineHeight: '1.5',
                            marginBottom: '1rem'
                        }}>
                            *Mutual Fund investments are subject to market risks. Please read all scheme related documents carefully before investing. Past performance is not indicative of future returns. Please consider your specific investment requirements before choosing a fund, or designing a portfolio that suits your needs.
                        </p>
                        <p style={{
                            fontSize: '0.875rem',
                            color: '#6b7280',
                            lineHeight: '1.5'
                        }}>
                            WinNest is an AMFI Registered Mutual Fund Distributor (ARN-81431). Registration granted by AMFI and certification from NISM in no way guarantee performance of the intermediary or provide any assurance of returns to investors.
                        </p>
                    </div>
                </section>

                {/* Footer */}
                <Footer />
            </div>
        </>
    );
};
export default OurApproach;